import React, { useState } from 'react';
import { useHasPermission } from '../stores/authStore';
import ReportsDashboard from '../components/reports/ReportsDashboard';
import AnalyticsDashboard from '../components/reports/AnalyticsDashboard';
import ReportWizard from '../components/reports/ReportWizard';
import ReportDraftViewer from '../components/reports/ReportDraftViewer';
import { Button } from '../components/ui';
import Card from '../components/ui/Card';
import Select from '../components/forms/Select';
import Input from '../components/forms/Input';
import { useChildren, useCreateReport, useDownloadReport } from '../hooks/api';
import { useDraftReportNarrative } from '../hooks/api/useReports';
import { ReportRequestCreate, ReportType, ReportFormat } from '../types/api';

// Map wizard report types to API report types
const WIZARD_TYPE_MAP: Record<string, ReportType> = {
  individual: 'student_progress',
  classroom: 'classroom_report',
  'assessment-matrix': 'assessment_overview',
  analytics: 'observation_summary',
};

// Map wizard format to API format
const FORMAT_MAP: Record<string, ReportFormat> = {
  pdf: 'pdf',
  docx: 'pdf', // API doesn't support docx, fallback to PDF
  online: 'html',
};

const Reports: React.FC = () => {
  const [currentView, setCurrentView] = useState<'dashboard' | 'analytics' | 'ai-draft'>('dashboard');
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState<string>('');

  // ── AI draft form state ─────────────────────────────────────────────
  // Lightweight form: pick a child + period, hit Generate, view + edit
  // the LLM-drafted narrative. The actual heavy lifting is in
  // ReportDraftViewer + the local-LLM service on the API.
  const [draftChildId, setDraftChildId] = useState('');
  const [draftDateFrom, setDraftDateFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().slice(0, 10);
  });
  const [draftDateTo, setDraftDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const draftNarrative = useDraftReportNarrative();
  const childrenQuery = useChildren();

  // Permissions
  // The backend permission code is `report.generate` (see DEFAULT_ROLES
  // in sprout-api). Older code here checked `report.create`, which
  // doesn't exist as a permission -- result was that no non-admin
  // user (lead_teacher, teacher) could ever see "Create Report",
  // even when their role explicitly granted report.generate.
  const canCreateReports = useHasPermission('report.generate');
  const canViewReports = useHasPermission('report.view');
  const canViewAnalytics = useHasPermission('analytics.view');

  const createReport = useCreateReport();
  const downloadReport = useDownloadReport();

  const handleCreateReport = (type: string, _config?: any) => {
    if (type === 'wizard') {
      setIsWizardOpen(true);
    } else {
      setSelectedReportType(type);
      setIsWizardOpen(true);
    }
  };

  const handleGenerateReport = async (config: any) => {
    const payload: ReportRequestCreate = {
      report_type: WIZARD_TYPE_MAP[config.type] || 'student_progress',
      report_format: FORMAT_MAP[config.format] || 'pdf',
      child_id: config.childId || undefined,
      classroom_id: config.classroomId || undefined,
      start_date: config.dateRange?.start || undefined,
      end_date: config.dateRange?.end || undefined,
      title: config.title || undefined,
      description: config.description || undefined,
      parameters: JSON.stringify({
        includeObservations: config.includeObservations,
        includeAssessments: config.includeAssessments,
        includeMedia: config.includeMedia,
        includeRecommendations: config.includeRecommendations,
        learningAreaIds: config.learningAreaIds,
        metrics: config.metrics,
      }),
    };

    await createReport.mutateAsync(payload);
    setIsWizardOpen(false);
  };

  const handleViewReport = (reportId: string) => {
    downloadReport.mutate({
      id: reportId,
      filename: `report-${reportId}.pdf`,
    });
  };

  const handleDrillDown = (_type: string, _filters: any) => {
    // Navigate to detailed analytics view
  };

  const handleExportData = (_type: string, _data: any) => {
    // Export analytics data
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* View Toggle */}
      <div className="mb-6 flex flex-wrap items-center gap-2 sm:gap-4">
        <Button
          variant={currentView === 'dashboard' ? 'primary' : 'ghost'}
          onClick={() => setCurrentView('dashboard')}
        >
          Reports Dashboard
        </Button>
        {canViewAnalytics && (
          <Button
            variant={currentView === 'analytics' ? 'primary' : 'ghost'}
            onClick={() => setCurrentView('analytics')}
          >
            Analytics
          </Button>
        )}
        <Button
          variant={currentView === 'ai-draft' ? 'primary' : 'ghost'}
          onClick={() => setCurrentView('ai-draft')}
          data-testid="ai-draft-tab"
        >
          ✨ AI Draft
        </Button>
      </div>

      {/* Main Content */}
      {currentView === 'dashboard' ? (
        <ReportsDashboard
          onCreateReport={handleCreateReport}
          onViewReport={handleViewReport}
          canCreateReports={canCreateReports}
          canViewReports={canViewReports}
        />
      ) : currentView === 'analytics' ? (
        <AnalyticsDashboard
          onDrillDown={handleDrillDown}
          onExportData={handleExportData}
        />
      ) : (
        <Card>
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                AI-drafted term report
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Pick a child and a period; the local AI will summarise their
                observations and assessments into a draft you can edit. The
                draft is never auto-published — you stay in control.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Child
                </label>
                <Select
                  value={draftChildId}
                  onChange={(e) => setDraftChildId(e.target.value)}
                  data-testid="ai-draft-child-select"
                  options={[
                    { value: '', label: 'Select a child…' },
                    ...((childrenQuery.data?.items ?? []).map((c: any) => ({
                      value: c.id,
                      label: `${c.first_name ?? c.name ?? ''} ${c.last_name ?? c.surname ?? ''}`.trim(),
                    }))),
                  ]}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  From
                </label>
                <Input
                  type="date"
                  value={draftDateFrom}
                  onChange={(e) => setDraftDateFrom(e.target.value)}
                  data-testid="ai-draft-date-from"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  To
                </label>
                <Input
                  type="date"
                  value={draftDateTo}
                  onChange={(e) => setDraftDateTo(e.target.value)}
                  data-testid="ai-draft-date-to"
                />
              </div>
            </div>

            <Button
              variant="primary"
              data-testid="ai-draft-generate"
              disabled={!draftChildId || draftNarrative.isPending}
              onClick={() =>
                draftNarrative.mutate({
                  child_id: draftChildId,
                  date_from: new Date(draftDateFrom).toISOString(),
                  date_to: new Date(draftDateTo).toISOString(),
                })
              }
            >
              {draftNarrative.isPending ? 'Drafting (this can take 10-60s)…' : '✨ Generate AI draft'}
            </Button>

            {draftNarrative.isError && (
              <div className="text-sm text-rose-600 dark:text-rose-400">
                Failed to generate draft. Try again or write the report manually.
              </div>
            )}

            {draftNarrative.data && (
              <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
                <ReportDraftViewer
                  draft={draftNarrative.data}
                  onClose={() => draftNarrative.reset()}
                />
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Report Wizard */}
      <ReportWizard
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        onGenerate={handleGenerateReport}
        initialType={selectedReportType}
      />
    </div>
  );
};

export default Reports;
